import Link from '@/components/compat/Link';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from '@/lib/navigation';
import { ArrowLeft, Loader2 } from 'lucide-react';
import Navbar from '@/components/help-center/Navbar';
import Footer from '@/components/help-center/Footer';
import { AceDetailSections } from '@/components/help-center/aces/AceDetailSections';
import { AceDetailRelated } from '@/components/help-center/aces/AceDetailRelated';
import { AceHero } from '@/components/help-center/aces/AceHero';
import { HeroBackdropLayers } from '@/components/help-center/latest-power-shared';
import { useCatalogAces } from '@/components/painel/aces/aces-cms-context';
import type { AceInfo } from '@/lib/aces-data';
import type { AceCmsRecord } from '@/lib/aces-cms';
import * as acesService from '@/services/aces.service';
import { isPubliclyVisible } from '@/lib/cms-publication';
import { CmsPublicEditButton } from '@/components/help-center/CmsPublicEditButton';

export default function AceDetailPage() {
  const { slug = '' } = useParams<{ slug: string }>();
  const { aces, hydrated } = useCatalogAces();
  const fromCatalog = useMemo(
    () => aces.find((a) => a.slug === slug.toLowerCase()),
    [aces, slug]
  );
  const [remoteAce, setRemoteAce] = useState<AceInfo | null>(null);
  const [remoteChecked, setRemoteChecked] = useState(false);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    setRemoteChecked(false);

    void acesService
      .getPublicAce(slug)
      .then((row) => {
        if (cancelled) return;
        if (row && isPubliclyVisible(row.publicationStatus)) {
          setRemoteAce(row as AceInfo);
        } else {
          setRemoteAce(null);
        }
      })
      .catch(() => {
        if (!cancelled) setRemoteAce(null);
      })
      .finally(() => {
        if (!cancelled) setRemoteChecked(true);
      });

    return () => {
      cancelled = true;
    };
  }, [slug]);

  const ace = useMemo(() => {
    if (remoteAce && fromCatalog) {
      const remoteTs = Date.parse(String((remoteAce as AceCmsRecord).updatedAt ?? '')) || 0;
      const localTs = Date.parse(String((fromCatalog as AceCmsRecord).updatedAt ?? '')) || 0;
      return localTs >= remoteTs ? fromCatalog : remoteAce;
    }
    return remoteAce ?? fromCatalog;
  }, [remoteAce, fromCatalog]);

  if (!hydrated && !remoteChecked && !ace) {
    return (
      <div className="flex min-h-screen flex-col bg-[#030023]">
        <Navbar />
        <div className="flex flex-1 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-amber-300" />
        </div>
        <Footer />
      </div>
    );
  }

  if (!ace) {
    return (
      <div className="flex min-h-screen flex-col bg-[#030023]">
        <Navbar />
        <div className="relative flex flex-1 items-center justify-center overflow-hidden">
          <div className="absolute inset-0" aria-hidden>
            <HeroBackdropLayers className="absolute inset-0" />
          </div>
          <div className="relative z-10 flex flex-col items-center gap-6 px-6 py-24 text-center">
            <h1 className="mb-2 font-poppins text-2xl font-bold text-white">Ace não encontrado</h1>
            <Link
              href="/aces"
              className="flex items-center gap-2 rounded-xl border border-amber-400/25 bg-amber-400/15 px-5 py-2.5 font-poppins text-sm font-semibold text-amber-300"
            >
              <ArrowLeft className="h-4 w-4" />
              Ver todos os Aces
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-white">
      <Navbar />
      <main className="flex-1">
        <AceHero
          variant="detail"
          ace={ace}
          editAction={
            <CmsPublicEditButton
              href={`/painel/aces/${ace.slug}/edit`}
              permission={['aces.edit', 'aces.review']}
              label="Editar ace"
              variant="hero"
            />
          }
        />
        <AceDetailSections ace={ace} />
        <AceDetailRelated ace={ace} />
      </main>
      <Footer />
    </div>
  );
}
